/**
 * Auth Manager Service
 *
 * Manages the dual-token authentication system:
 * - Access tokens (short-lived, 15 min) - stored in memory only
 * - Refresh tokens (long-lived, 30 days) - stored in IndexedDB with encryption
 *
 * Features:
 * - Automatic token refresh before expiration
 * - Prevents multiple simultaneous refresh attempts
 * - Handles token theft detection (server-side)
 * - Device fingerprinting for additional security
 */

import { tokenStorage } from './tokenStorage';
import { UserRole, OrganizationRole } from '@/types/enums';

export interface User {
  id: string;
  email: string;
  name?: string;
  role: UserRole;
  globalRole?: UserRole;
  orgRole?: OrganizationRole;
  parentId?: string;
  adminId?: string;
  currentOrganizationId?: string;
  emailVerified?: boolean;
  hasPasskey?: boolean;
  profilePhotoType?: string;
  profilePhotoUrl?: string | null;
  gravatarEmail?: string | null;
  orgProfilePhotoType?: string;
  orgProfilePhotoUrl?: string | null;
  orgGravatarEmail?: string | null;
  availableOrganizations?: Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    organizationSlug: string;
    role: OrganizationRole;
    subscriptionTier?: string;
  }>;
}

export interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  token?: string; // Backward compatibility
  user?: User;
}

type AuthStateListener = (state: AuthState) => void;

class AuthManager {
  private accessToken: string | null = null;
  private accessTokenExpiry: number = 0;
  private refreshPromise: Promise<boolean> | null = null;
  private user: User | null = null;
  private listeners: Set<AuthStateListener> = new Set();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized: boolean = false;

  /**
   * Initialize the auth manager
   * Call this on app startup to restore session
   */
  async initialize(): Promise<AuthState> {
    await tokenStorage.initialize();

    // Try to restore session from stored refresh token
    const refreshToken = await tokenStorage.getRefreshToken();

    if (refreshToken) {
      const success = await this.doRefresh(refreshToken);

      if (success && this.accessToken) {
        const user = await this.fetchCurrentUser();

        if (user) {
          this.user = user;
          this.initialized = true;
          this.scheduleTokenRefresh();

          const state = this.getState();
          this.notifyListeners(state);
          return state;
        }
      }
    }

    this.initialized = true;
    return this.getState();
  }

  /**
   * Login with email and password
   */
  async login(
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          deviceFingerprint: await this.getDeviceFingerprint(),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Login failed' }));
        return { success: false, error: error.message || error.error?.message || 'Login failed' };
      }

      const data: TokenResponse = await response.json();

      this.setAccessToken(data.accessToken, data.expiresIn);
      await tokenStorage.storeRefreshToken(data.refreshToken);

      if (data.user) {
        this.user = data.user;
      }

      this.scheduleTokenRefresh();
      this.notifyListeners(this.getState());

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  /**
   * Register a new account
   */
  async register(
    email: string,
    password: string,
    organizationName: string,
    selectedTier?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          organizationName,
          selectedTier,
          deviceFingerprint: await this.getDeviceFingerprint(),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Registration failed' }));
        return { success: false, error: error.message || error.error?.message || 'Registration failed' };
      }

      const data: TokenResponse = await response.json();

      this.setAccessToken(data.accessToken, data.expiresIn);
      await tokenStorage.storeRefreshToken(data.refreshToken);

      if (data.user) {
        this.user = data.user;
      }

      this.scheduleTokenRefresh();
      this.notifyListeners(this.getState());

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    const refreshToken = await tokenStorage.getRefreshToken();

    if (refreshToken && this.accessToken) {
      // Revoke on server (best effort)
      try {
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Ignore errors, still clear local state
      }
    }

    this.clearState();
    this.notifyListeners(this.getState());
  }

  /**
   * Get a valid access token
   * Automatically refreshes if expired or about to expire
   */
  async getAccessToken(): Promise<string | null> {
    // If token is valid and not about to expire (within 60s), return it
    if (this.accessToken && Date.now() < this.accessTokenExpiry - 60000) {
      return this.accessToken;
    }

    // All refresh attempts go through the same mutex
    if (this.refreshPromise) {
      await this.refreshPromise;
      return this.accessToken;
    }

    const refreshToken = await tokenStorage.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const success = await this.doRefresh(refreshToken);

    return success ? this.accessToken : null;
  }

  /**
   * Get the current access token synchronously (for SSE/EventSource)
   * Does NOT trigger refresh - use getAccessToken() first to ensure valid token
   * Returns null if no token or token is expired
   */
  getCurrentToken(): string | null {
    if (this.accessToken && Date.now() < this.accessTokenExpiry) {
      return this.accessToken;
    }
    return null;
  }

  /**
   * Get the current user
   */
  getUser(): User | null {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.accessToken !== null && this.user !== null;
  }

  /**
   * Get the current auth state
   */
  getState(): AuthState {
    return {
      accessToken: this.accessToken,
      user: this.user,
      isAuthenticated: this.isAuthenticated(),
      isLoading: !this.initialized,
    };
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: AuthStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Update the user data (e.g., after profile update or org switch)
   */
  updateUser(user: User): void {
    this.user = user;
    this.notifyListeners(this.getState());
  }

  /**
   * Update both token and user (e.g., after org switch)
   */
  async updateSession(accessToken: string, expiresIn: number, refreshToken: string, user: User): Promise<void> {
    this.setAccessToken(accessToken, expiresIn);
    await tokenStorage.storeRefreshToken(refreshToken);
    this.user = user;
    this.scheduleTokenRefresh();
    this.notifyListeners(this.getState());
  }

  /**
   * Swap the access token without changing the refresh token (e.g., after org switch).
   */
  updateAccessToken(accessToken: string, expiresIn: number = 86400): void {
    this.setAccessToken(accessToken, expiresIn);
  }

  // Private methods

  /**
   * Centralized refresh with mutex — all refresh paths go through here.
   * Prevents multiple concurrent refresh requests from racing each other.
   */
  private doRefresh(refreshToken: string): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.refreshAccessToken(refreshToken).finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async refreshAccessToken(refreshToken: string): Promise<boolean> {
    try {
      const response = await fetch('/api/v1/auth/token/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken,
          deviceFingerprint: await this.getDeviceFingerprint(),
        }),
      });

      if (!response.ok) {
        // Only clear state if we don't already have a valid token
        // (another concurrent refresh may have succeeded)
        if (!this.accessToken || Date.now() >= this.accessTokenExpiry) {
          this.clearState();
        }
        return false;
      }

      const data: TokenResponse = await response.json();

      this.setAccessToken(data.accessToken, data.expiresIn);
      await tokenStorage.storeRefreshToken(data.refreshToken);
      this.scheduleTokenRefresh();

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  private setAccessToken(token: string, expiresIn: number): void {
    this.accessToken = token;
    this.accessTokenExpiry = Date.now() + expiresIn * 1000;
  }

  private scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Calculate time until we should refresh (1 minute before expiry)
    const timeUntilRefresh = this.accessTokenExpiry - Date.now() - 60000;

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(async () => {
        // Don't refresh while the page is hidden — mobile browsers freeze background
        // tabs, so the server may rotate the token but the response gets dropped.
        // Instead, the next getAccessToken() call when the user returns will refresh.
        if (document.visibilityState === 'hidden') {
          return;
        }
        // Use getAccessToken() so we go through the mutex and skip if token is still valid
        await this.getAccessToken();
      }, timeUntilRefresh);
    }
  }

  private clearState(): void {
    this.accessToken = null;
    this.accessTokenExpiry = 0;
    this.user = null;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    tokenStorage.clearTokens();
  }

  private notifyListeners(state: AuthState): void {
    this.listeners.forEach(listener => listener(state));
  }

  private async fetchCurrentUser(): Promise<User | null> {
    const token = this.accessToken;
    if (!token) return null;

    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) return null;

      const data = await response.json();
      return data.user;
    } catch {
      return null;
    }
  }

  private async getDeviceFingerprint(): Promise<string> {
    // Simple fingerprint - can be enhanced as needed
    const data = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      new Date().getTimezoneOffset(),
    ].join('|');

    // crypto.subtle requires HTTPS (or localhost)
    // Fall back to simple hash if not available
    if (!window.crypto?.subtle) {
      // Simple fallback hash
      let hash = 0;
      for (let i = 0; i < data.length; i++) {
        const char = data.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash).toString(16).padStart(8, '0');
    }

    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}

export const authManager = new AuthManager();
